/**
 * ClientMeetingsTab — list/edit meetings for a client, with drag-and-drop
 * attachment uploads.
 *
 * Usage: <ClientMeetingsTab clientId={...} />
 */
import { useEffect, useRef, useState } from "react";
import {
  Plus, Trash, PencilSimple, FloppyDisk, X, Paperclip, Upload,
  CalendarBlank, Clock, MapPin, Users, DownloadSimple, FileText, Sparkle,
  MagicWand, Microphone, Link as LinkIcon, MagnifyingGlass,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatTz } from "@/lib/timezone";
import MarkdownField from "@/components/MarkdownField";
import { SafeHtml } from "@/lib/safeHtml";

const MAX_UPLOAD_MB = 200;

function toLocalInput(dtIso) {
  if (!dtIso) return "";
  try {
    const d = new Date(dtIso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ""; }
}

function fmtBytes(n) {
  if (n == null) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

const BLANK_MEETING = {
  title: "",
  meeting_at: "",
  duration_minutes: 60,
  location: "",
  summary: "",
  transcript: "",
  attendees: [],
};

/** "3 days ago" / "2 weeks ago" / "on 12 Mar 2026" — small helper used in
 *  the past-attendees dropdown so marketers can tell at a glance when
 *  they last met each person. No extra dependency. */
function shortRelative(iso) {
  if (!iso) return "";
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return "";
  const days = Math.floor((Date.now() - then.getTime()) / 86400000);
  if (days < 1) return "today";
  if (days < 2) return "yesterday";
  if (days < 14) return `${days}d ago`;
  if (days < 60) return `${Math.floor(days / 7)}w ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

export default function ClientMeetingsTab({ clientId, userTz, readOnly = false }) {
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // meeting id or 'new'
  const [form, setForm] = useState(BLANK_MEETING);
  const [saving, setSaving] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/clients/${clientId}/meetings`);
      setMeetings(r.data || []);
    } catch { toast.error("Failed to load meetings"); }
    setLoading(false);
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [clientId]);

  const startNew = () => {
    setForm({
      ...BLANK_MEETING,
      meeting_at: toLocalInput(new Date().toISOString()),
    });
    setEditing("new");
  };

  const startEdit = (m) => {
    setForm({
      title: m.title || "",
      meeting_at: toLocalInput(m.meeting_at),
      duration_minutes: m.duration_minutes || 60,
      location: m.location || "",
      summary: m.summary || "",
      transcript: m.transcript || "",
      attendees: (m.attendees || []).map((a) => ({
        name: a.name || "", email: a.email || "", organization: a.organization || "",
      })),
    });
    setEditing(m.id);
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    if (!form.meeting_at) { toast.error("Date/time is required"); return; }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        meeting_at: new Date(form.meeting_at).toISOString(),
        duration_minutes: Number(form.duration_minutes) || null,
        location: form.location || null,
        summary: form.summary || null,
        transcript: form.transcript || null,
        attendees: (form.attendees || []).filter((a) => a.name && a.name.trim()),
      };
      if (editing === "new") {
        const r = await api.post(`/clients/${clientId}/meetings`, payload);
        toast.success("Meeting added");
        setEditing(r.data.id); // jump to edit mode so attachments can be added
      } else {
        await api.patch(`/clients/${clientId}/meetings/${editing}`, payload);
        toast.success("Meeting updated");
      }
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  const doDelete = async (m) => {
    if (!window.confirm(`Delete meeting "${m.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/clients/${clientId}/meetings/${m.id}`);
      toast.success("Meeting deleted");
      if (editing === m.id) setEditing(null);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  if (loading) {
    return <p className="text-sm text-[#A3A3A3] p-4">Loading meetings…</p>;
  }

  const editingMeeting = editing && editing !== "new"
    ? meetings.find((m) => m.id === editing)
    : null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-4" data-testid="meetings-tab">
      {/* ── List column ──────────────────────────────────────────── */}
      <div className="border border-[#E5E5E5] bg-white">
        <div className="flex items-center justify-between px-3 py-2.5 border-b border-[#E5E5E5] bg-[#FAFAFA]">
          <h3 className="text-xs font-bold uppercase tracking-wider text-[#525252]">
            Meetings ({meetings.length})
            {readOnly && (
              <span className="ml-2 text-[10px] text-[#A3A3A3] normal-case font-normal">(read-only)</span>
            )}
          </h3>
          {!readOnly && (
            <button
              onClick={startNew}
              className="inline-flex items-center gap-1 bg-[#0055FF] text-white px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider hover:bg-[#0040CC]"
              data-testid="add-meeting-btn"
            >
              <Plus size={12} weight="bold" /> New
            </button>
          )}
        </div>
        <div className="max-h-[620px] overflow-y-auto">
          {meetings.length === 0 && (
            <p className="p-6 text-xs text-[#A3A3A3] text-center">
              {readOnly
                ? "No meetings have been recorded yet."
                : <>No meetings yet. Click <strong>New</strong> to record your first.</>}
            </p>
          )}
          {meetings.map((m) => {
            const active = editing === m.id;
            return (
              <button
                key={m.id}
                // Read-only users (clients viewing the project's
                // meetings tab) MUST be able to open a meeting so
                // they can scroll the transcript + read the summary.
                // The editor pane already handles viewer-mode
                // gating (`<fieldset disabled>` + no Save/Delete/
                // attach buttons) so opening the row is safe.
                onClick={() => startEdit(m)}
                className={`w-full text-left px-3 py-2.5 border-b border-[#F5F5F5] transition-colors ${active ? "bg-[#EFF6FF] border-l-2 border-l-[#0055FF]" : "hover:bg-[#FAFAFA]"} cursor-pointer`}
                data-testid={`meeting-row-${m.id}`}
              >
                <p className="text-sm font-semibold text-[#0A0A0A] truncate">{m.title}</p>
                <p className="text-[11px] text-[#525252] mt-0.5 flex items-center gap-2 flex-wrap">
                  <span className="inline-flex items-center gap-1">
                    <CalendarBlank size={11} /> {formatTz(m.meeting_at, userTz)}
                  </span>
                  {m.attendees?.length > 0 && (
                    <span className="inline-flex items-center gap-1">
                      <Users size={11} /> {m.attendees.length}
                    </span>
                  )}
                  {m.attachments?.length > 0 && (
                    <span className="inline-flex items-center gap-1">
                      <Paperclip size={11} /> {m.attachments.length}
                    </span>
                  )}
                </p>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Editor / Viewer column ───────────────────────────────── */}
      <div className="border border-[#E5E5E5] bg-white">
        {!editing ? (
          <div className="p-12 text-center text-[#A3A3A3]">
            <CalendarBlank size={40} className="mx-auto mb-3 opacity-40" />
            <p className="text-sm">Select a meeting on the left, or click <strong>New</strong> to add one.</p>
          </div>
        ) : (
          <MeetingEditor
            form={form}
            setForm={setForm}
            onSave={save}
            saving={saving}
            onCancel={() => setEditing(null)}
            onDelete={editing !== "new" ? () => doDelete(editingMeeting) : null}
            clientId={clientId}
            meetingId={editing !== "new" ? editing : null}
            attachments={editingMeeting?.attachments || []}
            onAttachmentsChanged={reload}
            readOnly={readOnly}
          />
        )}
      </div>
    </div>
  );
}

/** Combobox showing attendees from this client's past meetings.
 *  Fetches `/clients/:id/meeting-attendees` on mount. Filters out anyone
 *  already listed in the current meeting (`existing`), and by type-ahead
 *  query on name / email / organization. Picking an option calls `onPick`
 *  with the full attendee record so the parent can prefill a new row.
 *  Hidden entirely when the client has no prior attendees. */
function PastAttendeePicker({ clientId, existing, onPick }) {
  const [options, setOptions] = useState([]);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    let cancelled = false;
    api.get(`/clients/${clientId}/meeting-attendees`)
      .then(r => { if (!cancelled) { setOptions(r.data || []); setLoaded(true); } })
      .catch(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, [clientId]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const existingKeys = new Set();
  (existing || []).forEach(a => {
    const email = (a.email || "").trim().toLowerCase();
    if (email) existingKeys.add(`email::${email}`);
    const name = (a.name || "").trim().toLowerCase();
    if (name) existingKeys.add(`name::${name}`);
  });

  const q = query.trim().toLowerCase();
  const filtered = options
    .filter(o => {
      const email = (o.email || "").toLowerCase();
      const name = (o.name || "").toLowerCase();
      if (email && existingKeys.has(`email::${email}`)) return false;
      if (!email && name && existingKeys.has(`name::${name}`)) return false;
      if (!q) return true;
      return name.includes(q) || email.includes(q) || (o.organization || "").toLowerCase().includes(q);
    })
    .slice(0, 12);

  // No history yet → render nothing (typing via Add attendee still works).
  if (loaded && options.length === 0) return null;

  return (
    <div ref={ref} className="relative mb-1.5" data-testid="past-attendee-picker">
      <div className="flex items-center gap-1.5 border border-[#E5E5E5] bg-white px-2 h-8 focus-within:border-[#0055FF]">
        <MagnifyingGlass size={11} className="text-[#A3A3A3]" />
        <input
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={loaded ? "Add from past attendees…" : "Loading past attendees…"}
          className="flex-1 h-full text-xs bg-transparent focus:outline-none"
          data-testid="past-attendee-search"
          disabled={!loaded}
        />
        {query && (
          <button type="button" onClick={() => setQuery("")}
                  className="text-[#A3A3A3] hover:text-[#525252]"
                  data-testid="past-attendee-clear">
            <X size={11} />
          </button>
        )}
      </div>
      {open && loaded && (
        <div className="absolute z-20 left-0 right-0 top-9 bg-white border border-[#E5E5E5] shadow-lg max-h-64 overflow-y-auto"
             data-testid="past-attendee-dropdown">
          {filtered.length === 0 ? (
            <p className="p-3 text-[11px] text-[#A3A3A3] italic">
              {q ? "No match." : "Everyone from past meetings is already added."}
            </p>
          ) : (
            filtered.map((o, idx) => (
              <button
                key={`${o.email || ""}-${o.name || ""}-${idx}`}
                type="button"
                onClick={() => { onPick(o); setQuery(""); setOpen(false); }}
                className="w-full text-left px-3 py-2 hover:bg-[#FAFAFA] border-b border-[#F5F5F5] last:border-b-0"
                data-testid={`past-attendee-option-${idx}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs font-bold text-[#0A0A0A] truncate">{o.name || o.email}</p>
                  <span className="text-[9px] font-semibold text-[#A3A3A3] uppercase tracking-wider whitespace-nowrap">
                    {o.meeting_count}× · {shortRelative(o.last_seen_at)}
                  </span>
                </div>
                {(o.organization || o.email) && (
                  <p className="text-[10px] text-[#525252] truncate mt-0.5">
                    {o.organization || ""}
                    {o.organization && o.email ? " · " : ""}
                    {o.email || ""}
                  </p>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}


function MeetingEditor({ form, setForm, onSave, saving, onCancel, onDelete,
                        clientId, meetingId, attachments, onAttachmentsChanged,
                        readOnly = false }) {
  const update = (patch) => readOnly ? null : setForm({ ...form, ...patch });
  const updateAttendee = (i, patch) => {
    const next = [...(form.attendees || [])];
    next[i] = { ...next[i], ...patch };
    update({ attendees: next });
  };
  const addAttendee = (seed) =>
    update({
      attendees: [
        ...(form.attendees || []),
        { name: seed?.name || "", email: seed?.email || "", organization: seed?.organization || "" },
      ],
    });
  const removeAttendee = (i) => {
    const next = [...(form.attendees || [])];
    next.splice(i, 1);
    update({ attendees: next });
  };

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap border-b border-[#E5E5E5] pb-3">
        <h3 className="text-sm font-bold uppercase tracking-wider text-[#525252]">
          {readOnly ? "View Meeting" : (meetingId ? "Edit Meeting" : "New Meeting")}
        </h3>
        <div className="flex gap-2">
          {!readOnly && onDelete && (
            <button onClick={onDelete}
                    className="inline-flex items-center gap-1 px-3 py-1.5 border border-[#DC2626] text-[#DC2626] text-[11px] font-bold uppercase tracking-wider hover:bg-[#FEF2F2]"
                    data-testid="delete-meeting-btn">
              <Trash size={12} /> Delete
            </button>
          )}
          <button onClick={onCancel}
                  className="inline-flex items-center gap-1 px-3 py-1.5 border border-[#E5E5E5] text-[#525252] text-[11px] font-bold uppercase tracking-wider hover:border-[#A3A3A3]"
                  data-testid="cancel-meeting-btn">
            <X size={12} /> {readOnly ? "Close" : "Cancel"}
          </button>
          {!readOnly && (
            <button onClick={onSave} disabled={saving}
                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#0055FF] text-white text-[11px] font-bold uppercase tracking-wider hover:bg-[#0040CC] disabled:opacity-50"
                    data-testid="save-meeting-btn">
              <FloppyDisk size={12} /> {saving ? "Saving…" : "Save"}
            </button>
          )}
        </div>
      </div>

      <fieldset disabled={readOnly} className={readOnly ? "opacity-90" : ""}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="md:col-span-2">
          <Label>Title *</Label>
          <input
            value={form.title}
            onChange={(e) => update({ title: e.target.value })}
            placeholder="Kickoff call with All-State Legal"
            className="w-full h-9 px-3 border border-[#E5E5E5] text-sm"
            data-testid="meeting-title"
          />
        </div>
        <div>
          <Label>Date &amp; Time *</Label>
          <input
            type="datetime-local"
            value={form.meeting_at}
            onChange={(e) => update({ meeting_at: e.target.value })}
            className="w-full h-9 px-3 border border-[#E5E5E5] text-sm"
            data-testid="meeting-datetime"
          />
        </div>
        <div>
          <Label>Duration (min)</Label>
          <input
            type="number" min={0} step={15}
            value={form.duration_minutes || ""}
            onChange={(e) => update({ duration_minutes: Number(e.target.value) || null })}
            className="w-full h-9 px-3 border border-[#E5E5E5] text-sm tabular-nums"
            data-testid="meeting-duration"
          />
        </div>
        <div className="md:col-span-2">
          <Label>Location / Link</Label>
          <input
            value={form.location}
            onChange={(e) => update({ location: e.target.value })}
            placeholder="Zoom / Google Meet / office address"
            className="w-full h-9 px-3 border border-[#E5E5E5] text-sm"
            data-testid="meeting-location"
          />
        </div>
      </div>

      {/* Attendees */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <Label>Attendees</Label>
          {!readOnly && (
            <button onClick={() => addAttendee()}
                    className="text-[10px] font-bold uppercase tracking-wider text-[#0055FF] hover:underline inline-flex items-center gap-1"
                    data-testid="add-attendee-btn">
              <Plus size={10} weight="bold" /> Add attendee
            </button>
          )}
        </div>
        {!readOnly && (
          <PastAttendeePicker
            clientId={clientId}
            existing={form.attendees || []}
            onPick={(a) => addAttendee({
              name: a.name || "",
              email: a.email || "",
              organization: a.organization || "",
            })}
          />
        )}
        {(form.attendees || []).length === 0 ? (
          <p className="text-[11px] text-[#A3A3A3] italic mt-1.5">No attendees yet.</p>
        ) : (
          <div className="space-y-1.5 mt-1.5">
            {form.attendees.map((a, i) => (
              readOnly ? (
                // Read-only: render as a single line of text rather
                // than a row of editable inputs. Prevents the
                // visual noise of disabled-but-rendered text fields
                // and matches the "no controls they can't action"
                // policy.
                <div key={i} className="text-xs text-[#0A0A0A] py-0.5 flex items-center gap-2 flex-wrap" data-testid={`attendee-row-${i}`}>
                  <span className="font-bold">{a.name || "(no name)"}</span>
                  {a.email && <span className="text-[#525252]">{a.email}</span>}
                  {a.organization && <span className="text-[#A3A3A3]">· {a.organization}</span>}
                </div>
              ) : (
                <div key={i} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-1.5" data-testid={`attendee-row-${i}`}>
                  <input value={a.name} onChange={(e) => updateAttendee(i, { name: e.target.value })}
                        placeholder="Name" className="h-8 px-2 text-xs border border-[#E5E5E5]" />
                  <input value={a.email || ""} onChange={(e) => updateAttendee(i, { email: e.target.value })}
                        placeholder="Email" className="h-8 px-2 text-xs border border-[#E5E5E5]" />
                  <input value={a.organization || ""} onChange={(e) => updateAttendee(i, { organization: e.target.value })}
                        placeholder="Organization" className="h-8 px-2 text-xs border border-[#E5E5E5]" />
                  <button onClick={() => removeAttendee(i)}
                          className="p-1 text-[#525252] hover:text-[#DC2626]"
                          data-testid={`remove-attendee-${i}`}>
                    <Trash size={12} />
                  </button>
                </div>
              )
            ))}
          </div>
        )}
      </div>

      {/* Summary + transcript */}
      <FindInMeeting summary={form.summary} transcript={form.transcript} />
      <div>
        <div className="flex items-center justify-between mb-1">
          <Label>Summary</Label>
          {/* Re-summarise button only renders when the user can
              actually mutate the summary. Read-only viewers (clients
              on the Project Detail meetings tab) skip this. */}
          {!readOnly && meetingId && form.transcript && form.transcript.trim().length > 20 && (
            <SummarizeButton
              clientId={clientId}
              meetingId={meetingId}
              onDone={(summary) => update({ summary })}
            />
          )}
        </div>
        <MarkdownField
          value={form.summary || ""}
          onChange={(v) => update({ summary: v })}
          rows={5}
          placeholder={readOnly ? "(no summary yet)" : "Short recap of what was decided & next steps. **Bold**, lists, etc."}
          testId="meeting-summary"
          readOnly={readOnly}
        />
      </div>
      <div>
        <div className="flex items-center justify-between mb-1 flex-wrap gap-1.5">
          <Label>Transcript</Label>
          {/* Transcribe wand only renders for users who can write —
              the underlying endpoint is `_block_client_writes`-gated
              anyway, but hiding the button avoids tempting clients
              with a control they can't use. */}
          {!readOnly && meetingId && (
            <TranscribeButton
              clientId={clientId}
              meetingId={meetingId}
              hasTranscript={!!(form.transcript && form.transcript.trim())}
              attachments={attachments}
              onDone={({ transcript, summary }) => {
                update({
                  transcript,
                  ...(summary ? { summary } : {}),
                });
              }}
            />
          )}
        </div>
        <MarkdownField
          value={form.transcript || ""}
          onChange={(v) => update({ transcript: v })}
          rows={8}
          placeholder={readOnly ? "(no transcript yet)" : "Paste the full transcript or meeting notes here — or drop an audio/video file below and Pulse AI will transcribe it."}
          testId="meeting-transcript"
          monospace
          readOnly={readOnly}
        />
      </div>

      {/* Attachments. Read-only viewers see the file list (so they
          can click through to existing recordings/PDFs) but the
          drag-and-drop area + Delete buttons are hidden via the
          `readOnly` prop on AttachmentDropzone. */}
      {meetingId ? (
        <AttachmentDropzone
          clientId={clientId}
          meetingId={meetingId}
          attachments={attachments}
          hasTranscript={!!(form.transcript && form.transcript.trim())}
          onChanged={onAttachmentsChanged}
          onTranscribeResult={({ transcript, summary }) => {
            update({
              transcript,
              ...(summary ? { summary } : {}),
            });
          }}
          readOnly={readOnly}
        />
      ) : (
        !readOnly && <p className="text-[11px] text-[#A3A3A3] italic">Save the meeting first to attach files.</p>
      )}
      </fieldset>
    </div>
  );
}

function Label({ children }) {
  return (
    <label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#525252] block mb-1">
      {children}
    </label>
  );
}

const AUDIO_EXT_RE = /\.(mp3|m4a|mp4|mpeg|mpga|wav|webm|mov)$/i;
const isAudioVideo = (name) => AUDIO_EXT_RE.test(name || "");

function AttachmentDropzone({ clientId, meetingId, attachments, hasTranscript,
                              onChanged, onTranscribeResult, readOnly = false }) {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [transcribingId, setTranscribingId] = useState(null);
  const fileInput = useRef(null);

  /** After the /transcribe endpoint returns (fast — it only waits for
   *  Whisper), the backend keeps enriching the meeting asynchronously:
   *  speaker diarization and the auto-summary both run in a background
   *  task so the HTTP request doesn't die at the 60-100 s edge timeout.
   *  This helper polls the meeting GET every 4 s (up to ~2 min) and
   *  lifts the enriched transcript + summary back into the form. */
  const pollForEnrichment = async ({ diarize, summarize, initialTranscript }) => {
    if (!diarize && !summarize) return;
    const maxTries = 30;
    let tries = 0;
    while (tries++ < maxTries) {
      await new Promise((r) => setTimeout(r, 4000));
      try {
        const m = await api.get(`/clients/${clientId}/meetings/${meetingId}`);
        const data = m.data || {};
        const transcriptChanged =
          diarize && data.transcript && data.transcript !== initialTranscript;
        const haveSummary = summarize && (data.summary || "").trim().length > 0;
        if (transcriptChanged || haveSummary) {
          onTranscribeResult?.({
            transcript: data.transcript || initialTranscript,
            summary: data.summary || "",
          });
          if (transcriptChanged && haveSummary) {
            toast.success("Speakers attributed and summary generated");
            return;
          }
          if (transcriptChanged) toast.success("Speakers attributed");
          if (haveSummary) toast.success("Summary generated");
          // Keep polling until BOTH arrive, if both were requested.
          if ((!diarize || transcriptChanged) && (!summarize || haveSummary)) return;
        }
      } catch {
        // transient fetch failure — just keep polling
      }
    }
  };

  const runTranscribe = async ({ attachment_id, url, replace }) => {
    setTranscribingId(attachment_id || "url");
    const toastId = toast.loading("Transcribing with Pulse AI — this can take a minute for long recordings…");
    try {
      const r = await api.post(
        `/clients/${clientId}/meetings/${meetingId}/transcribe`,
        {
          attachment_id: attachment_id || undefined,
          url: url || undefined,
          replace_existing: !!replace,
          auto_summarize: true,
        },
      );
      onTranscribeResult?.({
        transcript: r.data.transcript,
        summary: r.data.summary,
      });
      const queued = r.data.post_processing?.status === "queued";
      toast.success(
        queued
          ? "Transcript saved. Generating summary in the background…"
          : "Transcript generated",
        { id: toastId },
      );
      pollForEnrichment({
        diarize: false,
        summarize: true,
        initialTranscript: r.data.transcript,
      });
    } catch (e) {
      // Long recordings frequently exceed the edge ingress timeout
      // (~60-100 s), so the SPA loses the response even though the
      // backend stored the transcript successfully. Before surfacing
      // a "failed" toast, poll the meeting endpoint to see if a
      // fresh transcript actually landed — if it did, treat the
      // run as a success and lift it into the UI. The "Whisper
      // returned an empty transcript" / 4xx errors are real
      // failures and propagate as before.
      const detail = e.response?.data?.detail || "";
      const isEdgeTimeout = !e.response  // axios network/abort error
        || e.code === "ECONNABORTED"
        || e.code === "ERR_NETWORK"
        || (e.response && [502, 503, 504].includes(e.response.status));
      let recovered = false;
      if (isEdgeTimeout) {
        toast.loading("Connection dropped — checking if the transcript landed…", { id: toastId });
        for (let tries = 0; tries < 45 && !recovered; tries++) {
          // 45 × 4 s ≈ 3 min — covers a 2-hour recording with a
          // generous safety margin (Whisper averages ~10 % wall-clock
          // time at the 32 kbps mono encoding the chunker emits).
          await new Promise(r => setTimeout(r, 4000));
          try {
            const m = await api.get(`/clients/${clientId}/meetings/${meetingId}`);
            const t = (m.data?.transcript || "").trim();
            if (t.length > 0 && (replace || !hasTranscript || t.length > 0)) {
              onTranscribeResult?.({
                transcript: m.data.transcript,
                summary: m.data.summary || null,
              });
              toast.success(
                m.data.summary?.trim()
                  ? "Transcript + summary recovered (the request lost its connection but the work completed)."
                  : "Transcript recovered. Summary still generating in the background…",
                { id: toastId },
              );
              if (!m.data.summary?.trim()) {
                pollForEnrichment({
                  diarize: false,
                  summarize: true,
                  initialTranscript: m.data.transcript,
                });
              }
              recovered = true;
              break;
            }
          } catch {
            // ignore and keep polling
          }
        }
      }
      if (!recovered) {
        toast.error(detail || "Transcription failed", { id: toastId });
      }
    } finally {
      setTranscribingId(null);
    }
  };

  const upload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    const uploaded = [];
    for (const file of Array.from(files)) {
      if (file.size > MAX_UPLOAD_MB * 1024 * 1024) {
        toast.error(`${file.name}: exceeds ${MAX_UPLOAD_MB} MB limit`);
        continue;
      }
      const fd = new FormData();
      fd.append("file", file);
      try {
        const r = await api.post(
          `/clients/${clientId}/meetings/${meetingId}/attachments`,
          fd,
          { headers: { "Content-Type": "multipart/form-data" } },
        );
        toast.success(`${file.name} uploaded`);
        uploaded.push({ id: r.data.id, file_name: r.data.file_name });
      } catch (e) {
        toast.error(`${file.name}: ${e.response?.data?.detail || "upload failed"}`);
      }
    }
    setUploading(false);
    onChanged();
    // Auto-transcribe: if exactly one audio/video file was dropped and there
    // is no transcript yet, kick off the pipeline immediately.
    const audio = uploaded.filter(u => isAudioVideo(u.file_name));
    if (audio.length === 1 && !hasTranscript) {
      await runTranscribe({ attachment_id: audio[0].id });
    } else if (audio.length === 1 && hasTranscript) {
      const ok = window.confirm(
        `You already have a transcript for this meeting. Replace it with the AI-generated transcript from ${audio[0].file_name}?`
      );
      if (ok) await runTranscribe({ attachment_id: audio[0].id, replace: true });
    } else if (audio.length > 1) {
      toast.info(`${audio.length} audio files uploaded. Click the wand icon on one to transcribe.`);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    upload(e.dataTransfer.files);
  };

  const del = async (a) => {
    if (!window.confirm(`Delete ${a.file_name}?`)) return;
    try {
      await api.delete(`/clients/${clientId}/meetings/${meetingId}/attachments/${a.id}`);
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const onTranscribeAttachment = async (a) => {
    if (hasTranscript) {
      const ok = window.confirm(
        `Replace the existing transcript with an AI transcript of ${a.file_name}?`
      );
      if (!ok) return;
    }
    await runTranscribe({ attachment_id: a.id, replace: hasTranscript });
  };

  return (
    <div data-testid="attachment-dropzone">
      <Label>Attachments ({attachments.length})</Label>
      {/* Read-only viewers see the file LIST (so they can click
          through to existing recordings/transcripts) but the
          drag-and-drop zone is hidden — uploading attachments is a
          mutating operation gated on the backend by
          _block_client_writes. */}
      {!readOnly && (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          onClick={() => fileInput.current?.click()}
          className={`cursor-pointer border-2 border-dashed ${dragging ? "border-[#0055FF] bg-[#EFF6FF]" : "border-[#E5E5E5] bg-[#FAFAFA]"} p-6 text-center transition-colors`}
          data-testid="attachment-drop-area"
        >
          <Upload size={22} className="mx-auto mb-1.5 text-[#525252]" weight={dragging ? "fill" : "regular"} />
          <p className="text-sm font-semibold text-[#0A0A0A]">
            {uploading ? "Uploading…" : "Drop files here or click to browse"}
          </p>
          <p className="text-[11px] text-[#A3A3A3] mt-0.5">
            Audio/video (.mp3, .m4a, .mp4, .wav, .webm, .mov) is auto-transcribed by Pulse AI. Up to {MAX_UPLOAD_MB} MB each.
          </p>
          <input
            ref={fileInput}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => { upload(e.target.files); e.target.value = ""; }}
            data-testid="attachment-file-input"
          />
        </div>
      )}

      {attachments.length > 0 && (
        <ul className="mt-2 divide-y divide-[#F5F5F5] border border-[#E5E5E5]">
          {attachments.map((a) => {
            const canTranscribe = isAudioVideo(a.file_name);
            const busy = transcribingId === a.id;
            return (
              <li key={a.id} className="px-3 py-2 flex items-center gap-2 text-sm hover:bg-[#FAFAFA]"
                  data-testid={`attachment-${a.id}`}>
                {canTranscribe
                  ? <Microphone size={14} className="text-[#0055FF] shrink-0" weight="duotone" />
                  : <FileText size={14} className="text-[#525252] shrink-0" />}
                <span className="flex-1 truncate text-[#0A0A0A]">{a.file_name}</span>
                <span className="text-[10px] text-[#A3A3A3] tabular-nums shrink-0">{fmtBytes(a.file_size)}</span>
                {/* Per-row transcribe wand only renders for users who
                    can actually mutate the meeting. Read-only viewers
                    keep the download link but lose the wand and the
                    trash icon. */}
                {!readOnly && canTranscribe && (
                  <button
                    onClick={() => onTranscribeAttachment(a)}
                    disabled={busy || transcribingId !== null}
                    className="p-1 text-[#0055FF] hover:text-[#0040CC] disabled:opacity-40"
                    title="Transcribe with Pulse AI"
                    data-testid={`attachment-transcribe-${a.id}`}
                  >
                    <MagicWand size={14} weight={busy ? "fill" : "regular"} className={busy ? "animate-pulse" : ""} />
                  </button>
                )}
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/clients/${clientId}/meetings/${meetingId}/attachments/${a.id}/download`}
                   target="_blank" rel="noreferrer"
                   className="p-1 text-[#525252] hover:text-[#0055FF]" title="Download"
                   data-testid={`attachment-download-${a.id}`}>
                  <DownloadSimple size={14} />
                </a>
                {!readOnly && (
                  <button onClick={() => del(a)} className="p-1 text-[#525252] hover:text-[#DC2626]"
                          data-testid={`attachment-delete-${a.id}`}>
                    <Trash size={14} />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function TranscribeButton({ clientId, meetingId, hasTranscript, attachments, onDone }) {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const audioAttachments = (attachments || []).filter(a => isAudioVideo(a.file_name));

  const run = async ({ attachment_id, url }) => {
    if (hasTranscript) {
      const ok = window.confirm("Replace the existing transcript with a new AI transcript?");
      if (!ok) return;
    }
    setBusy(true);
    setOpen(false);
    const toastId = toast.loading("Transcribing with Pulse AI — this can take a minute for long recordings…");
    try {
      const r = await api.post(
        `/clients/${clientId}/meetings/${meetingId}/transcribe`,
        { attachment_id, url, replace_existing: hasTranscript, auto_summarize: true, diarize: true },
      );
      // Show raw transcript right away — diarize + summary arrive via polling.
      onDone({ transcript: r.data.transcript, summary: r.data.summary });
      const queued = r.data.post_processing?.status === "queued";
      toast.success(
        queued
          ? "Transcript saved. Attributing speakers & generating summary in the background…"
          : "Transcript generated",
        { id: toastId },
      );
      // Poll every 4s for up to 2 min while the background task enriches.
      if (queued) {
        const initial = r.data.transcript;
        for (let tries = 0; tries < 30; tries++) {
          await new Promise((res) => setTimeout(res, 4000));
          try {
            const m = await api.get(`/clients/${clientId}/meetings/${meetingId}`);
            const changed = m.data?.transcript && m.data.transcript !== initial;
            const haveSum = (m.data?.summary || "").trim().length > 0;
            if (changed || haveSum) {
              onDone({ transcript: m.data.transcript || initial, summary: m.data.summary || "" });
              if (changed && haveSum) { toast.success("Speakers attributed and summary generated"); break; }
              if (changed)  toast.success("Speakers attributed");
              if (haveSum)  toast.success("Summary generated");
              if (changed && haveSum) break;
            }
          } catch { /* transient — keep polling */ }
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Transcription failed", { id: toastId });
    }
    setBusy(false);
  };

  const diarize = async () => {
    setBusy(true);
    setOpen(false);
    const toastId = toast.loading("Attributing speakers with Pulse AI…");
    try {
      const r = await api.post(`/clients/${clientId}/meetings/${meetingId}/diarize`);
      onDone({ transcript: r.data.transcript });
      toast.success("Speakers attributed", { id: toastId });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Diarization failed", { id: toastId });
    }
    setBusy(false);
  };

  const askUrl = () => {
    const url = window.prompt("Public download URL to the audio/video file:");
    if (!url) return;
    run({ url: url.trim() });
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        disabled={busy}
        className="inline-flex items-center gap-1 px-2 h-7 bg-[#0055FF] text-white text-[10px] font-bold uppercase tracking-wider hover:bg-[#0040CC] disabled:opacity-50"
        data-testid="transcribe-meeting-btn"
      >
        <MagicWand size={11} weight="fill" />
        {busy ? "Transcribing…" : "Transcribe with AI"}
      </button>
      {open && !busy && (
        <div className="absolute right-0 top-8 z-20 bg-white border border-[#E5E5E5] shadow-lg min-w-[260px]"
             onMouseLeave={() => setOpen(false)}
             data-testid="transcribe-menu">
          <button onClick={askUrl}
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-left hover:bg-[#FAFAFA]"
                  data-testid="transcribe-menu-url">
            <LinkIcon size={12} className="text-[#525252]" />
            <span className="flex-1">From a URL…</span>
          </button>
          {audioAttachments.length > 0 && (
            <>
              <div className="border-t border-[#F5F5F5] px-3 py-1 text-[9px] uppercase tracking-wider text-[#A3A3A3]">
                From an attachment
              </div>
              {audioAttachments.map(a => (
                <button
                  key={a.id}
                  onClick={() => run({ attachment_id: a.id })}
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-left hover:bg-[#FAFAFA] border-t border-[#F5F5F5]"
                  data-testid={`transcribe-menu-attach-${a.id}`}
                >
                  <Microphone size={12} className="text-[#0055FF]" weight="duotone" />
                  <span className="flex-1 truncate">{a.file_name}</span>
                  <span className="text-[10px] text-[#A3A3A3] tabular-nums">{fmtBytes(a.file_size)}</span>
                </button>
              ))}
            </>
          )}
          {hasTranscript && (
            <>
              <div className="border-t border-[#F5F5F5] px-3 py-1 text-[9px] uppercase tracking-wider text-[#A3A3A3]">
                On the current transcript
              </div>
              <button onClick={diarize}
                      className="w-full flex items-center gap-2 px-3 py-2 text-xs text-left hover:bg-[#FAFAFA] border-t border-[#F5F5F5]"
                      data-testid="diarize-btn">
                <Users size={12} className="text-[#0055FF]" />
                <span className="flex-1">Attribute speakers</span>
              </button>
            </>
          )}
          {audioAttachments.length === 0 && !hasTranscript && (
            <div className="border-t border-[#F5F5F5] px-3 py-2 text-[11px] text-[#A3A3A3] italic">
              No audio/video attachments yet — drop one below to auto-transcribe.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SummarizeButton({ clientId, meetingId, onDone }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await api.post(`/clients/${clientId}/meetings/${meetingId}/summarize`);
      onDone(r.data.summary);
      toast.success("Summary generated with Pulse AI");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Summarize failed");
    }
    setBusy(false);
  };
  return (
    <button
      type="button"
      onClick={run}
      disabled={busy}
      className="inline-flex items-center gap-1 px-2 h-7 bg-[#0A0A0A] text-white text-[10px] font-bold uppercase tracking-wider hover:bg-[#0055FF] disabled:opacity-50"
      data-testid="summarize-meeting-btn"
    >
      <Sparkle size={11} weight="fill" />
      {busy ? "Summarizing…" : "Summarize with Pulse AI"}
    </button>
  );
}



/* ───────── Find in meeting ─────────
 *  Searches the meeting's summary + transcript, returns snippet matches
 *  with the query highlighted. Keeps the textareas editable; this lives
 *  above them as a read-only results pane.
 */
function FindInMeeting({ summary, transcript }) {
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState(false);

  const results = q.trim().length >= 2
    ? findSnippets(q.trim(), { Summary: summary, Transcript: transcript })
    : null;
  const total = results ? results.reduce((n, r) => n + r.matches.length, 0) : 0;

  return (
    <div className="border border-[#E5E5E5] bg-[#FAFAFA]" data-testid="find-in-meeting">
      <div className="flex items-center gap-2 p-2">
        <MagnifyingGlass size={13} className="text-[#525252]" />
        <input
          type="text"
          value={q}
          onChange={(e) => { setQ(e.target.value); setExpanded(true); }}
          placeholder="Find in this meeting — e.g. 'Oracle integration', 'action item', a name…"
          className="flex-1 bg-transparent text-xs outline-none"
          data-testid="meeting-find-input"
        />
        {q && (
          <span className="text-[10px] text-[#A3A3A3] tabular-nums" data-testid="meeting-find-count">
            {total} match{total === 1 ? "" : "es"}
          </span>
        )}
        {q && (
          <button onClick={() => { setQ(""); setExpanded(false); }}
                  className="p-1 text-[#525252] hover:text-[#0A0A0A]"
                  data-testid="meeting-find-clear">
            <X size={12} />
          </button>
        )}
      </div>
      {results && expanded && total > 0 && (
        <div className="border-t border-[#E5E5E5] bg-white px-3 py-2 space-y-2 max-h-64 overflow-y-auto"
             data-testid="meeting-find-results">
          {results.map(group =>
            group.matches.map((m, i) => (
              <div key={`${group.label}-${i}`} className="text-[11px]">
                <span className="text-[9px] font-bold uppercase tracking-wider text-[#A3A3A3]">{group.label}</span>
                {/* Meeting transcript highlight snippet. Backend crm.py runs
                     sanitize_comment_html on the stored transcript; SafeHtml
                     with tight=true reapplies the tight allowlist so nothing
                     beyond simple inline formatting + <mark> survives. */}
                <SafeHtml
                  as="p"
                  tight
                  className="text-[#0A0A0A] mt-0.5"
                  html={m}
                />
              </div>
            ))
          )}
        </div>
      )}
      {results && expanded && total === 0 && (
        <div className="border-t border-[#E5E5E5] bg-white px-3 py-2 text-[11px] text-[#A3A3A3] italic">
          No matches in this meeting.
        </div>
      )}
    </div>
  );
}

function findSnippets(q, sources) {
  // Escape regex special chars in the query, case-insensitive.
  const safe = q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const re = new RegExp(safe, "gi");
  const out = [];
  for (const [label, text] of Object.entries(sources)) {
    if (!text) continue;
    const matches = [];
    let m;
    while ((m = re.exec(text)) !== null && matches.length < 12) {
      const start = Math.max(0, m.index - 60);
      const end = Math.min(text.length, m.index + m[0].length + 60);
      const before = escapeHtml(text.slice(start, m.index));
      const hit = `<mark style="background:#FEF08A;padding:0 2px;">${escapeHtml(m[0])}</mark>`;
      const after = escapeHtml(text.slice(m.index + m[0].length, end));
      const prefix = start > 0 ? "…" : "";
      const suffix = end < text.length ? "…" : "";
      matches.push(`${prefix}${before}${hit}${after}${suffix}`);
      if (m.index === re.lastIndex) re.lastIndex++;
    }
    if (matches.length > 0) out.push({ label, matches });
  }
  return out;
}

function escapeHtml(s) {
  return (s || "")
    .replace(/&/g, "&amp;").replace(/</g, "&lt;")
    .replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}
